import { Suspense, lazy } from 'react'
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import PageNotFound from './lib/PageNotFound';
import Layout from './components/layout/Layout';
import Home from './pages/Home';

// Home ships in the entry bundle — it's the landing page for most visits and the
// one Core Web Vitals are measured on. Every other route is split out, so the
// first load no longer parses 24 pages nobody has navigated to yet.
const Services = lazy(() => import('./pages/Services'));
const Gallery = lazy(() => import('./pages/Gallery'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const KitchenRemodeling = lazy(() => import('./pages/services/KitchenRemodeling'));
const BathroomRemodeling = lazy(() => import('./pages/services/BathroomRemodeling'));
const HomeAdditions = lazy(() => import('./pages/services/HomeAdditions'));
const CustomBuilds = lazy(() => import('./pages/services/CustomBuilds'));
const InteriorFinishes = lazy(() => import('./pages/services/InteriorFinishes'));
const EVChargerInstallation = lazy(() => import('./pages/services/EVChargerInstallation'));
const Roofing = lazy(() => import('./pages/services/Roofing'));
const Flooring = lazy(() => import('./pages/services/Flooring'));
const Painting = lazy(() => import('./pages/services/Painting'));
const HomeSystemsUpgrades = lazy(() => import('./pages/services/HomeSystemsUpgrades'));
const ADACompliance = lazy(() => import('./pages/services/ADACompliance'));
const CommercialRemodeling = lazy(() => import('./pages/services/CommercialRemodeling'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const AccessibilityStatement = lazy(() => import('./pages/AccessibilityStatement'));
const LeaveAReview = lazy(() => import('./pages/LeaveAReview'));
const LocationPage = lazy(() => import('./pages/locations/LocationPage'));

// Holds the page background while a route chunk arrives, so the swap doesn't
// flash white against the near-black theme.
function RouteFallback() {
  return <div className="min-h-screen bg-[#0a0a0a]" aria-hidden="true" />;
}

const AppRoutes = () => {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/services/kitchen-remodeling" element={<KitchenRemodeling />} />
        <Route path="/services/bathroom-remodeling" element={<BathroomRemodeling />} />
        <Route path="/services/home-additions" element={<HomeAdditions />} />
        <Route path="/services/custom-builds" element={<CustomBuilds />} />
        <Route path="/services/interior-finishes" element={<InteriorFinishes />} />
        <Route path="/services/ev-charger-installation" element={<EVChargerInstallation />} />
        <Route path="/services/roofing" element={<Roofing />} />
        <Route path="/services/flooring" element={<Flooring />} />
        <Route path="/services/painting" element={<Painting />} />
        <Route path="/services/home-systems-upgrades" element={<HomeSystemsUpgrades />} />
        <Route path="/services/ada-compliance" element={<ADACompliance />} />
        <Route path="/services/commercial-remodeling" element={<CommercialRemodeling />} />
        <Route path="/locations/:city" element={<LocationPage />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/accessibility" element={<AccessibilityStatement />} />
        <Route path="/leave-a-review" element={<LeaveAReview />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
};


function App() {

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <Suspense fallback={<RouteFallback />}>
            <AppRoutes />
          </Suspense>
        </Router>
        <Toaster />
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
